import { useState, useEffect, useCallback } from 'react';
import { supabase, safeSubscribe } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

/**
 * useNotifications - Hook para gerenciar notificações do usuário
 *
 * Fluxo simplificado: notificação não lida → usuário lê → some da lista.
 * lido_at é a única coluna de controle (fonte única de verdade).
 *
 * @param {Function} onNewNotification - Callback chamado quando nova notificação chega (opcional)
 * @returns {Object} { notifications, unreadCount, loading, markAsRead, markAllAsRead, refetch }
 */
export function useNotifications(onNewNotification?: (n: any) => void) {
  const { pessoa } = useAuth();
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Buscar notificações não lidas
  const fetchNotifications = useCallback(async () => {
    if (!pessoa?.id) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('notificacao')
        .select(`
          *,
          autor:created_by(id, nome, apelido)
        `)
        .eq('pessoa_id', pessoa.id)
        .is('lido_at', null)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      setNotifications(data || []);
      setUnreadCount((data || []).length);
    } catch (err) {
      console.error('Erro ao carregar notificações:', err);
    } finally {
      setLoading(false);
    }
  }, [pessoa?.id]);

  // Carregar notificações ao montar e quando pessoa mudar
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Subscribe para novas notificações em tempo real
  useEffect(() => {
    if (!pessoa?.id) return;

    const subscription = supabase
      .channel(`notificacoes:${pessoa.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notificacao',
          filter: `pessoa_id=eq.${pessoa.id}`
        },
        (payload) => {
          const newNotification = payload.new;
          setNotifications(prev => [newNotification, ...prev]);
          setUnreadCount(prev => prev + 1);

          if (onNewNotification) {
            onNewNotification(newNotification);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notificacao',
          filter: `pessoa_id=eq.${pessoa.id}`
        },
        (payload) => {
          // Se foi lida, remover da lista
          setNotifications(prev => {
            const updated = prev.filter(n => n.id !== payload.new.id || !payload.new.lido_at);
            setUnreadCount(updated.length);
            return updated;
          });
        }
      );

    safeSubscribe(subscription);

    return () => {
      subscription.unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- onNewNotification intentionally omitted to prevent infinite re-subscribe loops
  }, [pessoa?.id]);

  // Marcar como lida (ler = sair da lista)
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notificacao')
        .update({ lido_at: new Date().toISOString() })
        .eq('id', notificationId);

      if (error) throw error;

      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Erro ao marcar como lida:', err);
    }
  }, []);

  // Marcar todas como lidas (limpa a lista inteira)
  const markAllAsRead = useCallback(async () => {
    if (!pessoa?.id) return;

    try {
      const { error } = await supabase
        .from('notificacao')
        .update({ lido_at: new Date().toISOString() })
        .eq('pessoa_id', pessoa.id)
        .is('lido_at', null);

      if (error) throw error;

      setNotifications([]);
      setUnreadCount(0);
    } catch (err) {
      console.error('Erro ao marcar todas como lidas:', err);
    }
  }, [pessoa?.id]);

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    refetch: fetchNotifications
  };
}

export default useNotifications;
